import { useState } from "react";
import * as Sentry from "@sentry/react";
import {
  Link,
  TextField,
  FormControl,
  Alert,
  InputLabel,
  useTheme,
} from "@mui/material";

import { NavLink as RouterLink, useNavigate } from "react-router-dom";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import WatchLaterIcon from "@mui/icons-material/WatchLater";

import { LoadingButton } from "@mui/lab";

import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { motion, AnimatePresence } from "framer-motion";

import { useFormik } from "formik";
import { object, string } from "yup";

import PageHeader from "../PageHeader";

import Logo from "../Logo";
import { useShowNetworkFailureToast } from "../NetworkFailureToast";
import { useTrueBizApi } from "../../api";

const MCCSearchSchema = object({
  businessName: string().required("A business name is required"),
  website: string()
    .test(
      "website",
      "Enter a valid website (e.g. example.com)",
      function (value) {
        if (!value) return true;

        try {
          new URL((value as string).trim());
          return true;
        } catch (e) {
          // pass
        }

        return false;
      }
    )
    .nullable(),
  city: string().nullable(),
  stateProvince: string().nullable(),
  postalCode: string().nullable(),
  country: string().required("A country is required"),
});

export type FormState = ReturnType<typeof MCCSearchSchema.validateSync>;

export default function LookupMCC() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const showToast = useShowNetworkFailureToast();
  const navigate = useNavigate();
  const api = useTrueBizApi();

  const initialAdvanced = {
    businessName: "",
    website: "",
    city: "",
    stateProvince: "",
    postalCode: "",
    country: "",
  };

  const theme = useTheme();

  const advancedSearchForm = useFormik({
    initialValues: {
      businessName: initialAdvanced?.businessName || "",
      website: initialAdvanced?.website || "",
      city: initialAdvanced?.city || "",
      stateProvince: initialAdvanced?.stateProvince || "",
      postalCode: initialAdvanced?.postalCode || "",
      country: initialAdvanced?.country || "",
    },
    onSubmit: async (values) => {
      try {
        setIsSubmitting(true);
        const search = await api.submitBusinessSearch(values);
        navigate(`/mcc-discovery/${search.id}`);
      } catch (e) {
        Sentry.captureException(e);
        console.error(e);
        showToast(
          "There was a problem running your search. Please try again shortly."
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    validationSchema: MCCSearchSchema,
  });

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        width: "100vw",
        paddingLeft: "1em",
        paddingRight: "1em",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
      }}
    >
      <PageHeader
        logo={false}
        navLinks={false}
        divider={false}
        style={{ position: "absolute", top: 0 }}
      />
      <Logo height="55px" style={{ marginBottom: "1.5em" }} />
      <div
        style={{
          marginBottom: "1em",
          display: "flex",
          gap: "1.5em",
          justifyContent: "center",
        }}
      >
        <RouterLink
          to="/"
          style={{
            color: theme.palette.primary.main,
            textDecoration: "none",
          }}
        >
          Web Presence
        </RouterLink>
        <div
          style={{
            color: theme.palette.grey[800],
            borderBottom: `2px solid ${theme.palette.primary.main}`,
          }}
        >
          <strong>MCC Discovery</strong>
        </div>
      </div>

      <AnimatePresence>
        <motion.form
          style={{
            width: "100%",
            maxWidth: "550px",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          key="advanced"
          onSubmit={advancedSearchForm.handleSubmit}
        >
          <div style={{ marginTop: "2em" }}>
            <FormControl sx={{ display: "flex" }}>
              <TextField
                fullWidth
                name="businessName"
                onChange={advancedSearchForm.handleChange}
                value={advancedSearchForm.values.businessName}
                label="Business Name"
                type="text"
                inputProps={{ tabIndex: 1 }}
                autoFocus
                error={
                  !!advancedSearchForm.errors.businessName &&
                  advancedSearchForm.touched.businessName
                }
                disabled={isSubmitting}
              />
              <AnimatePresence>
                {advancedSearchForm.errors.businessName &&
                  advancedSearchForm.touched.businessName && (
                    <motion.div
                      key="errors-businessName"
                      initial={{
                        height: 0,
                        opacity: 0,
                      }}
                      animate={{
                        height: "auto",
                        opacity: 1,
                      }}
                      exit={{
                        height: 0,
                        opacity: 0,
                      }}
                    >
                      <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                        {advancedSearchForm.errors.businessName}
                      </Alert>
                    </motion.div>
                  )}
              </AnimatePresence>
            </FormControl>
          </div>
          <div
            style={{
              display: "flex",
              gap: "1.25em",
              marginTop: "2.5em",
            }}
          >
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: "1em",
              }}
            >
              <InputLabel>Locality</InputLabel>
              <div style={{ display: "flex", gap: "1em" }}>
                <FormControl sx={{ display: "flex", flex: 1 }}>
                  <TextField
                    fullWidth
                    name="city"
                    onChange={advancedSearchForm.handleChange}
                    value={advancedSearchForm.values.city}
                    label="City"
                    type="text"
                    inputProps={{ tabIndex: 2 }}
                    error={
                      !!advancedSearchForm.errors.city &&
                      advancedSearchForm.touched.city
                    }
                    disabled={isSubmitting}
                  />
                  <AnimatePresence>
                    {advancedSearchForm.errors.city &&
                      advancedSearchForm.touched.city && (
                        <motion.div
                          key="errors-city"
                          initial={{
                            height: 0,
                            opacity: 0,
                          }}
                          animate={{
                            height: "auto",
                            opacity: 1,
                          }}
                          exit={{
                            height: 0,
                            opacity: 0,
                          }}
                        >
                          <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                            {advancedSearchForm.errors.city}
                          </Alert>
                        </motion.div>
                      )}
                  </AnimatePresence>
                </FormControl>
                <FormControl sx={{ display: "flex", flex: 1 }}>
                  <TextField
                    fullWidth
                    name="stateProvince"
                    onChange={advancedSearchForm.handleChange}
                    value={advancedSearchForm.values.stateProvince}
                    label="State/Province"
                    type="text"
                    inputProps={{ tabIndex: 3 }}
                    error={
                      !!advancedSearchForm.errors.stateProvince &&
                      advancedSearchForm.touched.stateProvince
                    }
                    disabled={isSubmitting}
                  />
                  <AnimatePresence>
                    {advancedSearchForm.errors.stateProvince &&
                      advancedSearchForm.touched.stateProvince && (
                        <motion.div
                          key="errors-stateProvince"
                          initial={{
                            height: 0,
                            opacity: 0,
                          }}
                          animate={{
                            height: "auto",
                            opacity: 1,
                          }}
                          exit={{
                            height: 0,
                            opacity: 0,
                          }}
                        >
                          <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                            {advancedSearchForm.errors.stateProvince}
                          </Alert>
                        </motion.div>
                      )}
                  </AnimatePresence>
                </FormControl>
              </div>
              <div style={{ display: "flex", gap: "1em" }}>
                <FormControl sx={{ display: "flex", flex: 1 }}>
                  <TextField
                    fullWidth
                    name="postalCode"
                    onChange={advancedSearchForm.handleChange}
                    value={advancedSearchForm.values.postalCode}
                    label="Postal Code"
                    type="text"
                    inputProps={{ tabIndex: 4 }}
                    error={
                      !!advancedSearchForm.errors.postalCode &&
                      advancedSearchForm.touched.postalCode
                    }
                    disabled={isSubmitting}
                  />
                  <AnimatePresence>
                    {advancedSearchForm.errors.postalCode &&
                      advancedSearchForm.touched.postalCode && (
                        <motion.div
                          key="errors-postalCode"
                          initial={{
                            height: 0,
                            opacity: 0,
                          }}
                          animate={{
                            height: "auto",
                            opacity: 1,
                          }}
                          exit={{
                            height: 0,
                            opacity: 0,
                          }}
                        >
                          <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                            {advancedSearchForm.errors.postalCode}
                          </Alert>
                        </motion.div>
                      )}
                  </AnimatePresence>
                </FormControl>
                <FormControl sx={{ display: "flex", flex: 1 }}>
                  <TextField
                    fullWidth
                    name="country"
                    onChange={advancedSearchForm.handleChange}
                    value={advancedSearchForm.values.country}
                    label="Country"
                    type="text"
                    inputProps={{ tabIndex: 5 }}
                    error={
                      !!advancedSearchForm.errors.country &&
                      advancedSearchForm.touched.country
                    }
                    disabled={isSubmitting}
                  />
                  <AnimatePresence>
                    {advancedSearchForm.errors.country &&
                      advancedSearchForm.touched.country && (
                        <motion.div
                          key="errors-country"
                          initial={{
                            height: 0,
                            opacity: 0,
                          }}
                          animate={{
                            height: "auto",
                            opacity: 1,
                          }}
                          exit={{
                            height: 0,
                            opacity: 0,
                          }}
                        >
                          <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                            {advancedSearchForm.errors.country}
                          </Alert>
                        </motion.div>
                      )}
                  </AnimatePresence>
                </FormControl>
              </div>
            </div>
          </div>
          <div style={{ marginTop: "2.5em" }}>
            <FormControl sx={{ display: "flex" }}>
              <TextField
                fullWidth
                name="website"
                onChange={advancedSearchForm.handleChange}
                value={advancedSearchForm.values.website}
                label="Website"
                type="text"
                inputProps={{ tabIndex: 6 }}
                error={
                  !!advancedSearchForm.errors.website &&
                  advancedSearchForm.touched.website
                }
                disabled={isSubmitting}
              />
              <AnimatePresence>
                {advancedSearchForm.errors.website &&
                  advancedSearchForm.touched.website && (
                    <motion.div
                      key="errors-website"
                      initial={{
                        height: 0,
                        opacity: 0,
                      }}
                      animate={{
                        height: "auto",
                        opacity: 1,
                      }}
                      exit={{
                        height: 0,
                        opacity: 0,
                      }}
                    >
                      <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                        {advancedSearchForm.errors.website}
                      </Alert>
                    </motion.div>
                  )}
              </AnimatePresence>
            </FormControl>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "2em",
            }}
          >
            <LoadingButton
              startIcon={<SearchOutlinedIcon />}
              variant={"contained"}
              sx={{
                textTransform: "unset",
                fontSize: "unset",
              }}
              tabIndex={7}
              type="submit"
              loading={isSubmitting}
            >
              Search
            </LoadingButton>
          </div>
        </motion.form>
      </AnimatePresence>
      <div
        style={{
          position: "absolute",
          bottom: 0,
          display: "flex",
          flexDirection: "row",
          gap: "2em",
          padding: "1em",
        }}
      >
        <Link
          component={RouterLink}
          to="/monitoring"
          sx={{
            display: "flex",
            gap: "0.25em",
            color: "#8E8E8E",
            textDecoration: "none",
          }}
        >
          <MonitorHeartIcon style={{ color: "#D3D3D3" }} />
          <span>Monitoring</span>
        </Link>
        <Link
          component={RouterLink}
          to="/searches"
          sx={{
            display: "flex",
            gap: "0.25em",
            color: "#8E8E8E",
            textDecoration: "none",
          }}
        >
          <WatchLaterIcon style={{ color: "#D3D3D3" }} />
          <span>Search History</span>
        </Link>
      </div>
    </div>
  );
}
